import { reactive, computed } from 'vue';
import { useClient } from '@/lib/_API/useClient';

export const MODEL_REGISTRY_PIN = (): outbound.registry.pin => ({
  name: '',
  phoneNumber: '',
  jibunAddress: '',
  roadAddress: '',
  detailAddress: '',
  dongName: '',
  postalCode: '',
  latitude: 0,
  longitude: 0,
  pickupTime: '',
  phoneValidate: false,
  comment: '',
});

export default function useMassRegistry() {
  return {
    state,
    ...useMassRegistryAction(),
    ...useMassRegistryUpdate(),
    ...useMassRegistryGetters(),
  };
}

/**
 * 다량등기접수 전용 상태 값
 */
export const state = reactive<outbound.registry.stateMass>({
  origin: MODEL_REGISTRY_PIN(),
  list: [],
  isBusy: false,
  policy: false,
  customFields: [],
});

/**
 * 다량등기접수 상태 값을 단순 업데이트 합니다.
 */
export function useMassRegistryUpdate() {
  /**
   * 출발지 정보 업데이트
   */
  const updateOrigin = <T extends outbound.registry.pin, K extends keyof T>(key: K, value: T[K]) => {
    (state.origin as any)[key as string] = value;
  };

  /**
   * 출발지 정보 초기화
   */
  const resetOrigin = <T extends outbound.registry.pin, K extends keyof T>() => {
    const key = Object.keys(MODEL_REGISTRY_PIN()) as Array<K>;
    const value = Object.values(MODEL_REGISTRY_PIN()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateOrigin(el, value[index]));
  };

  const updateCustomFields = (value: outbound.customFields[]) => {
    state.customFields = value;
  };

  return {
    updateOrigin,
    resetOrigin,
    updateCustomFields,
  };
}

/**
 *
 * 다량등기접수에 사용 되는 공통 action을 정의합니다.
 */
export function useMassRegistryAction() {
  const { updateOrigin, resetOrigin, updateCustomFields } = useMassRegistryUpdate();
  const { getClientInfo } = useClient();
  /**
   * 출발지 정보 로그인한 사용자 정보로 업데이트
   */
  const actionOriginUpdateUser = async () => {
    try {
      const { client, center } = await getClientInfo();
      updateOrigin('name', client['name']);
      updateOrigin('phoneNumber', client['phoneNumber'] as string);
      updateOrigin('dongName', center['dongName']);
      updateOrigin('jibunAddress', center['jibunAddress']);
      updateOrigin('roadAddress', center['roadAddress']);
      updateOrigin('detailAddress', center['detailAddress']);
      updateOrigin('latitude', center['latitude']);
      updateOrigin('longitude', center['longitude']);
      updateOrigin('postalCode', center['postalCode']);
    } catch {}
  };

  /**
   * onUnmounted에 전체 초기화
   */
  const actionOnUnmouned = () => {
    state.isBusy = false;
    state.policy = false;
    state.list = [];
    resetOrigin();
    updateCustomFields([]);
  };

  return {
    actionOriginUpdateUser,
    actionOnUnmouned,
  };
}

/**
 * 다량등기접수 getters
 */
export function useMassRegistryGetters() {
  /**
   * 발송리스트 중 에러가 발생한 목록이 있는지 확인합니다.
   */
  const getListHaveError = computed(() => {
    for (let i = 0; i < state.list.length; i++) {
      if (state.list[i].isError === true) return true;
    }
    return false;
  });

  /**
   * 계산 완료된 건
   */
  const getChargeCount = computed(() => {
    return state.list.reduce((stack, el, i) => {
      if (el.isError === true) return stack;
      else return (stack += 1);
    }, 0);
  });

  /**
   * 계산 완료된 총 요금
   */
  const getChargeTotal = computed(() => {
    return state.list.reduce((stack, el, i) => {
      if (!el.chargeUuid) return stack;
      else return stack + el.totalCharge;
    }, 0);
  });

  return {
    getListHaveError,
    getChargeCount,
    getChargeTotal,
  };
}
